import { OPPORTUNITY_LABELS, WEBSITE_EVIDENCE_LABELS } from '@shared/opportunities.js';
import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArrowLeft,
  Ban,
  Copy as CopyIcon,
  ExternalLink,
  Globe,
  Pencil,
  Phone,
  Plus,
  Star,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Page } from '@/components/AppShell';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Label,
  LoadingState,
  Select,
  Textarea,
} from '@/components/ui/primitives';
import {
  ConfirmButton,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
} from '@/components/ui/overlays';
import { LeadFormDialog } from '@/components/LeadFormDialog';
import { WebsiteAuditTab } from '@/components/WebsiteAuditTab';
import { FollowUpDialog } from '@/components/FollowUpDialog';
import { useAuth } from '@/lib/auth';
import { CustomerPanel } from '@/components/CustomerPanel';
import {
  cn,
  copyToClipboard,
  formatDateTime,
  formatDuration,
  formatLocalDate,
  formatPhone,
  formatRelative,
  formatBusinessAge,
  formatStartDate,
  pipelineStatusClass,
  scoreClass,
  webPresenceClass,
} from '@/lib/utils';
import { ACTIVITY_LABELS, PIPELINE_STATUSES } from '@shared/enums.js';
import type { LeadOwnerEvidence } from '@shared/types.js';

export function LeadDetailPage() {
  const { id } = useParams();
  const leadId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { user, authEnabled } = useAuth();
  // A VA calls, notes, schedules and updates status. Everything else on this
  // page is an admin action the server refuses anyway.
  const isVa = authEnabled && user?.role === 'va';
  const [editOpen, setEditOpen] = React.useState(false);
  const [noteBody, setNoteBody] = React.useState('');
  const [followUpOpen, setFollowUpOpen] = React.useState(false);
  const [dncOpen, setDncOpen] = React.useState(false);

  const lead = useQuery({
    queryKey: ['lead', leadId],
    queryFn: () => api.getLead(leadId),
    enabled: Number.isFinite(leadId),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
    queryClient.invalidateQueries({ queryKey: ['leads'] });
  };

  const addNote = useMutation({
    mutationFn: () => api.addNote(leadId, noteBody.trim()),
    onSuccess: () => {
      setNoteBody('');
      invalidate();
      toast.success('Note added');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const setStatus = useMutation({
    mutationFn: (status: string) => api.setLeadStatus(leadId, status),
    onSuccess: () => {
      invalidate();
      toast.success('Status updated');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const setArchived = useMutation({
    mutationFn: (archived: boolean) => api.archiveLead(leadId, archived),
    onSuccess: () => {
      invalidate();
      toast.success('Archive state updated');
    },
  });

  const removeDnc = useMutation({
    mutationFn: () => api.setLeadDnc(leadId, false),
    onSuccess: () => {
      invalidate();
      toast.success('Do Not Contact removed');
    },
  });

  if (lead.isLoading) return <Page title="Lead"><LoadingState /></Page>;
  if (lead.error) {
    return (
      <Page title="Lead">
        <ErrorState error={lead.error} onRetry={() => lead.refetch()} />
      </Page>
    );
  }

  const detail = lead.data!;
  const l = detail.lead;

  return (
    <Page
      title={l.businessName}
      description={[l.city, l.state, detail.county?.name && `${detail.county.name} County`]
        .filter(Boolean)
        .join(' · ')}
      actions={
        <>
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-3.5 w-3.5" />
            Back
          </Button>
          {l.normalizedPhone && !l.doNotContact && (
            <Button asChild size="sm">
              <Link to={`/queue/lead-${l.id}`}>
                <Phone className="h-3.5 w-3.5" />
                Call
              </Link>
            </Button>
          )}
          {!isVa && (
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </Button>
          )}
        </>
      }
    >
      <div className="grid grid-cols-1 gap-5 p-5 md:p-6 xl:grid-cols-[23rem_minmax(0,1fr)]">
        {/* ---------------- Left column ---------------- */}
        <div className="space-y-3">
          {l.doNotContact && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-red-300">
                <Ban className="h-3.5 w-3.5" />
                Do Not Contact
              </p>
              {l.dncReason && <p className="mt-1 text-2xs text-muted-foreground">{l.dncReason}</p>}
              <p className="mt-1 text-2xs text-muted-foreground">Applied {formatDateTime(l.dncAt)}</p>
              <ConfirmButton
                title="Remove Do Not Contact?"
                description="This lead will become callable again and can re-enter queues."
                confirmLabel="Remove DNC"
                destructive={false}
                onConfirm={() => removeDnc.mutate()}
              >
                <Button variant="outline" size="xs" className="mt-2">
                  Remove DNC
                </Button>
              </ConfirmButton>
            </div>
          )}

          {l.mergedIntoLeadId && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs">
              This lead was merged into{' '}
              <Link to={`/leads/${l.mergedIntoLeadId}`} className="font-medium text-primary hover:underline">
                lead #{l.mergedIntoLeadId}
              </Link>
              .
            </div>
          )}

          <Card>
            <CardBody className="space-y-2.5">
              <div>
                <Label>Phone</Label>
                {l.primaryPhone ? (
                  <div className="flex items-center gap-2">
                    <span className="phone-lg">{formatPhone(l.primaryPhone)}</span>
                    <Tooltip content="Copy">
                      <button
                        onClick={async () => {
                          if (await copyToClipboard(l.normalizedPhone ?? l.primaryPhone ?? '')) {
                            toast.success('Phone copied');
                          }
                        }}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <CopyIcon className="h-3.5 w-3.5" />
                      </button>
                    </Tooltip>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No phone on file</p>
                )}
                {l.secondaryPhone && (
                  <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                    Alt {formatPhone(l.secondaryPhone)}
                  </p>
                )}
              </div>

              <InfoRow label="Owner" value={l.ownerName} />
              <InfoRow label="Opportunity type" value={OPPORTUNITY_LABELS[l.opportunityType??'unclassified']} />
              <InfoRow label="Website evidence" value={WEBSITE_EVIDENCE_LABELS[l.websiteEvidenceStatus??'inconclusive']} />
              {l.websiteEvidenceAt && <InfoRow label="Website evidence date" value={new Date(l.websiteEvidenceAt*1000).toLocaleString()} />}
              <InfoRow label="Contact" value={l.contactName} />
              <InfoRow label="Email" value={l.email} />
              <InfoRow label="Address" value={l.streetAddress} />
              <InfoRow label="Category" value={detail.category?.name ?? null} />
              <InfoRow
                label="Counties served"
                value={
                  detail.serviceCounties.length
                    ? detail.serviceCounties.map((c) => c.name).join(', ')
                    : null
                }
              />
              {/*
                A month- or year-only start date is anchored on the first of the
                period internally. Showing that anchor as a full date would
                present an invented day as fact, so only the part that was
                actually established is rendered.
              */}
              <InfoRow
                label="Business age"
                value={
                  l.businessStartedDate
                    ? `${formatBusinessAge(l.businessStartedDate)} (started ${formatStartDate(
                        l.businessStartedDate,
                        l.businessStartedPrecision,
                      )})`
                    : null
                }
              />
              <InfoRow
                label="Rating"
                value={l.rating ? `${l.rating.toFixed(1)} from ${l.reviewCount ?? 0} reviews` : null}
              />

              <div>
                <Label>Lead status</Label>
                <Select
                  value={l.pipelineStatus}
                  onChange={(e) => setStatus.mutate(e.target.value)}
                  className="mt-0.5"
                >
                  {PIPELINE_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="flex flex-wrap gap-1.5 pt-1">
                <Badge className={cn('border', pipelineStatusClass(l.pipelineStatus))}>{l.pipelineStatus}</Badge>
                <Badge className={cn('border', webPresenceClass(l.webPresenceStatus))}>{l.webPresenceStatus}</Badge>
                {l.normalizedLeadScore !== null && (
                  <Badge className="border border-border">
                    <span className={scoreClass(l.normalizedLeadScore)}>Score {l.normalizedLeadScore}</span>
                  </Badge>
                )}
                {l.archivedAt && <Badge>Archived</Badge>}
              </div>
            </CardBody>
          </Card>

          {detail.scoreReasons.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Why this score</CardTitle>
                <span className="text-2xs text-muted-foreground">v{l.leadScoreVersion}</span>
              </CardHeader>
              <CardBody className="space-y-1">
                {detail.scoreReasons.map((reason) => (
                  <div key={reason.id} className="flex items-start justify-between gap-2 text-xs">
                    <div className="min-w-0">
                      <p className="truncate">{reason.label}</p>
                      {reason.detail && (
                        <p className="truncate text-2xs text-muted-foreground">{reason.detail}</p>
                      )}
                    </div>
                    <span
                      className={cn(
                        'shrink-0 font-semibold tabular-nums',
                        reason.points > 0 ? 'text-emerald-600 dark:text-emerald-300' : reason.points < 0 ? 'text-red-600 dark:text-red-300' : 'text-muted-foreground',
                      )}
                    >
                      {reason.points > 0 ? '+' : ''}
                      {reason.points}
                    </span>
                  </div>
                ))}
              </CardBody>
            </Card>
          )}

          {detail.ownerEvidence.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Owner evidence</CardTitle>
              </CardHeader>
              <CardBody className="space-y-2">
                {detail.ownerEvidence.map((e) => (
                  <div key={e.id} className="text-xs">
                    <div className="flex items-start justify-between gap-2">
                      <p className="min-w-0 truncate font-medium">
                        {e.personName}
                        {e.role && <span className="font-normal text-muted-foreground">, {e.role}</span>}
                      </p>
                      <Badge className={cn('shrink-0 border', ownerStatusClass(e.status))}>{OWNER_STATUS_LABEL[e.status]}</Badge>
                    </div>
                    {e.evidenceUrl ? (
                      <LinkRow label={ownerEvidenceHost(e.evidenceUrl)} url={e.evidenceUrl} />
                    ) : (
                      <p className="text-2xs text-muted-foreground">No page: a name recorded before owner checking.</p>
                    )}
                  </div>
                ))}
              </CardBody>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Links</CardTitle>
            </CardHeader>
            <CardBody className="space-y-1">
              <LinkRow label="Google profile" url={l.googleBusinessProfileUrl} />
              <LinkRow label="Facebook" url={l.facebookUrl} />
              <LinkRow label="BBB" url={l.bbbUrl} />
              <LinkRow label="Yelp" url={l.yelpUrl} />
              {detail.websites
                .filter((w) => w.isPrimary)
                .map((w) => (
                  <LinkRow key={w.id} label="Website" url={w.url} />
                ))}
              {detail.sources.map((s) => (
                <LinkRow key={s.id} label={s.sourceType} url={s.sourceUrl} />
              ))}
              {!l.googleBusinessProfileUrl &&
                !l.facebookUrl &&
                !l.bbbUrl &&
                !l.yelpUrl &&
                detail.websites.length === 0 &&
                detail.sources.length === 0 && (
                  <p className="text-xs text-muted-foreground">No links recorded.</p>
                )}
            </CardBody>
          </Card>

          {(detail.parentLead || detail.childLeads.length > 0) && (
            <Card>
              <CardHeader>
                <CardTitle>Locations</CardTitle>
              </CardHeader>
              <CardBody className="space-y-1 text-xs">
                {detail.parentLead && (
                  <p>
                    Location of{' '}
                    <Link to={`/leads/${detail.parentLead.id}`} className="text-primary hover:underline">
                      {detail.parentLead.businessName}
                    </Link>
                  </p>
                )}
                {detail.childLeads.map((child) => (
                  <p key={child.id}>
                    <Link to={`/leads/${child.id}`} className="text-primary hover:underline">
                      {child.businessName}
                    </Link>{' '}
                    <span className="text-muted-foreground">is a location of this lead</span>
                  </p>
                ))}
              </CardBody>
            </Card>
          )}

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setFollowUpOpen(true)}>
              <Plus className="h-3.5 w-3.5" />
              Follow-up
            </Button>
            {!l.doNotContact && (
              <Button variant="outline" size="sm" onClick={() => setDncOpen(true)}>
                <Ban className="h-3.5 w-3.5" />
                Apply DNC
              </Button>
            )}
            {!isVa && <ConfirmButton
              title={l.archivedAt ? 'Unarchive this lead?' : 'Archive this lead?'}
              description={
                l.archivedAt
                  ? 'It will appear in normal lists and queues again.'
                  : 'It will be hidden from lists and excluded from queues. Nothing is deleted.'
              }
              confirmLabel={l.archivedAt ? 'Unarchive' : 'Archive'}
              destructive={!l.archivedAt}
              onConfirm={() => setArchived.mutate(!l.archivedAt)}
            >
              <Button variant="outline" size="sm">
                <Archive className="h-3.5 w-3.5" />
                {l.archivedAt ? 'Unarchive' : 'Archive'}
              </Button>
            </ConfirmButton>}
          </div>
        </div>

        {/* ---------------- Right column ---------------- */}
        <div className="min-w-0">
          <Tabs defaultValue="overview">
            <TabsList className="w-full rounded-xl border border-border bg-card p-1 shadow-sm">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="websites">
                Website & Audit
                {l.openWebsiteIssueCount > 0 && <Badge variant="warning">{l.openWebsiteIssueCount}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="notes">
                Notes<Badge>{detail.notes.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="calls">
                Calls<Badge>{detail.callAttempts.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="followups">
                Follow-ups<Badge>{detail.followUps.filter((f) => f.status === 'Open').length}</Badge>
              </TabsTrigger>
              {!isVa && (
                <TabsTrigger value="customer">
                  Customer{detail.customer && <Badge variant="success">1</Badge>}
                </TabsTrigger>
              )}
              <TabsTrigger value="activity">Activity</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="pt-3">
              <div className="space-y-3">
                {l.outreachAngle && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Outreach angle</CardTitle>
                    </CardHeader>
                    <CardBody className="whitespace-pre-wrap text-sm">{l.outreachAngle}</CardBody>
                  </Card>
                )}
                {l.researchNotes && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Research notes</CardTitle>
                    </CardHeader>
                    <CardBody className="max-h-[32rem] overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed">{l.researchNotes}</CardBody>
                  </Card>
                )}
                <Card>
                  <CardHeader>
                    <CardTitle>Record</CardTitle>
                  </CardHeader>
                  <CardBody className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                    <InfoRow label="Created" value={formatDateTime(l.createdAt)} />
                    <InfoRow label="Updated" value={formatDateTime(l.updatedAt)} />
                    <InfoRow label="Imported" value={l.dateImported ? formatDateTime(l.dateImported) : null} />
                    <InfoRow label="Discovered" value={l.dateDiscovered ? formatLocalDate(l.dateDiscovered) : null} />
                    <InfoRow label="Last contacted" value={formatRelative(l.lastContactedAt)} />
                    <InfoRow label="Call attempts" value={String(detail.callAttempts.length)} />
                    <InfoRow label="Google Place ID" value={l.googlePlaceId} />
                    <InfoRow
                      label="Cooldown"
                      value={l.webPresenceStatus ? undefined : undefined}
                    />
                  </CardBody>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="websites" className="pt-3">
              <WebsiteAuditTab leadId={leadId} readOnly={isVa} />
            </TabsContent>

            <TabsContent value="notes" className="pt-3">
              <Card>
                <CardBody>
                  <Textarea
                    value={noteBody}
                    onChange={(e) => setNoteBody(e.target.value)}
                    placeholder="Add a note… (Ctrl+Enter to save)"
                    rows={3}
                    onKeyDown={(e) => {
                      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && noteBody.trim()) addNote.mutate();
                    }}
                  />
                  <div className="mt-2 flex justify-end">
                    <Button
                      size="sm"
                      disabled={!noteBody.trim()}
                      loading={addNote.isPending}
                      onClick={() => addNote.mutate()}
                    >
                      Add note
                    </Button>
                  </div>
                </CardBody>
              </Card>

              <div className="mt-3 space-y-2">
                {detail.notes.length === 0 && (
                  <EmptyState title="No notes yet" description="Notes are searchable from anywhere in LeadForge." />
                )}
                {detail.notes.map((note) => (
                  <Card key={note.id}>
                    <CardBody>
                      <div className="mb-1 flex items-center gap-2">
                        <Badge>{note.noteType}</Badge>
                        <span className="text-2xs text-muted-foreground">{formatDateTime(note.createdAt)}</span>
                        <ConfirmButton
                          title="Delete this note?"
                          confirmLabel="Delete"
                          onConfirm={async () => {
                            await api.deleteNote(note.id);
                            invalidate();
                            toast.success('Note deleted');
                          }}
                        >
                          <button className="ml-auto text-muted-foreground hover:text-red-300">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </ConfirmButton>
                      </div>
                      <p className="whitespace-pre-wrap text-sm">{note.noteBody}</p>
                    </CardBody>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="calls" className="pt-3">
              {detail.callAttempts.length === 0 ? (
                <EmptyState icon={Phone} title="No calls recorded" />
              ) : (
                <Card>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Outcome</th>
                        <th>Number</th>
                        {/*
                          Attribution. Blank for anything called before phone
                          lines existed, or by an agent with no active line —
                          "we do not know" is the honest answer, not a guess at
                          whichever line is current now.
                        */}
                        <th>Called by</th>
                        <th>From line</th>
                        <th className="text-right">Duration</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.callAttempts.map((call) => (
                        <tr key={call.id}>
                          <td className="whitespace-nowrap text-muted-foreground">
                            {formatDateTime(call.startedAt)}
                          </td>
                          <td>
                            <Badge variant="outline">{call.outcomeName}</Badge>
                          </td>
                          <td className="font-mono tabular-nums">{formatPhone(call.phoneNumber)}</td>
                          <td className="text-muted-foreground">{call.agentName ?? '—'}</td>
                          <td className="text-muted-foreground">
                            {call.lineLabel ? (
                              <Tooltip
                                content={
                                  call.linePhoneNumber
                                    ? formatPhone(call.linePhoneNumber.replace('+1', ''))
                                    : 'No number recorded for this line'
                                }
                              >
                                <span>{call.lineLabel}</span>
                              </Tooltip>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="text-right tabular-nums text-muted-foreground">
                            <Tooltip content="Approximate — a tel: handoff cannot report exact call length">
                              <span>~{formatDuration(call.approximateDurationSeconds)}</span>
                            </Tooltip>
                          </td>
                          <td className="max-w-md truncate text-muted-foreground">{call.notes ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="followups" className="pt-3">
              <div className="mb-3 flex justify-end">
                <Button size="sm" onClick={() => setFollowUpOpen(true)}>
                  <Plus className="h-3.5 w-3.5" />
                  Schedule follow-up
                </Button>
              </div>
              {detail.followUps.length === 0 ? (
                <EmptyState title="No follow-ups scheduled" />
              ) : (
                <Card>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Due</th>
                        <th>Priority</th>
                        <th>Status</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.followUps.map((fu) => (
                        <tr key={fu.id}>
                          <td>{fu.followUpType}</td>
                          <td className="whitespace-nowrap">
                            {formatLocalDate(fu.snoozedUntilDate ?? fu.dueDate)}
                            {fu.dueTime && <span className="ml-1 text-muted-foreground">{fu.dueTime}</span>}
                            {!fu.hasTime && <span className="ml-1 text-2xs text-muted-foreground">(all day)</span>}
                          </td>
                          <td>{fu.priority}</td>
                          <td>
                            <Badge variant={fu.status === 'Completed' ? 'success' : fu.status === 'Snoozed' ? 'warning' : 'primary'}>
                              {fu.status}
                            </Badge>
                          </td>
                          <td className="max-w-md truncate text-muted-foreground">{fu.notes ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              )}
            </TabsContent>

            {!isVa && (
              <TabsContent value="customer" className="pt-3">
                <CustomerPanel leadId={leadId} lead={detail} />
              </TabsContent>
            )}

            <TabsContent value="activity" className="pt-3">
              <Card>
                <CardBody className="space-y-2">
                  {detail.activities.length === 0 && <p className="text-xs text-muted-foreground">No activity yet.</p>}
                  {detail.activities.map((activity) => (
                    <div key={activity.id} className="flex gap-2 text-xs">
                      <span className="w-32 shrink-0 text-muted-foreground">
                        {formatDateTime(activity.createdAt)}
                      </span>
                      <Badge variant="outline" className="shrink-0">
                        {ACTIVITY_LABELS[activity.activityType] ?? activity.activityType}
                      </Badge>
                      <span className="min-w-0 flex-1">{activity.summary}</span>
                    </div>
                  ))}
                </CardBody>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <LeadFormDialog open={editOpen} onOpenChange={setEditOpen} lead={detail} />
      <FollowUpDialog open={followUpOpen} onOpenChange={setFollowUpOpen} leadId={leadId} />
      <DncDialog open={dncOpen} onOpenChange={setDncOpen} leadId={leadId} onDone={invalidate} />
    </Page>
  );
}

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <Label>{label}</Label>
      <p className="truncate text-sm">{value || <span className="text-muted-foreground">—</span>}</p>
    </div>
  );
}

const OWNER_STATUS_LABEL: Record<LeadOwnerEvidence['status'], string> = {
  verified_owner: 'Owner confirmed',
  role_confirmed_contact: 'Role confirmed',
  unresolved: 'Not confirmed',
  conflicting: 'Conflicts with entered name',
};

function ownerStatusClass(status: LeadOwnerEvidence['status']): string {
  if (status === 'verified_owner') return 'text-emerald-600 dark:text-emerald-300';
  if (status === 'role_confirmed_contact') return 'text-sky-600 dark:text-sky-300';
  if (status === 'conflicting') return 'text-red-600 dark:text-red-300';
  return 'text-muted-foreground';
}

function ownerEvidenceHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return 'Source page';
  }
}

function LinkRow({ label, url }: { label: string; url: string | null }) {
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer noopener"
      className="flex items-center gap-1.5 text-xs text-primary hover:underline"
    >
      {label === 'Website' ? <Globe className="h-3 w-3 shrink-0" /> : <ExternalLink className="h-3 w-3 shrink-0" />}
      <span className="truncate">{label}</span>
    </a>
  );
}

function DncDialog({
  open,
  onOpenChange,
  leadId,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: number;
  onDone: () => void;
}) {
  const [reason, setReason] = React.useState('');
  const apply = useMutation({
    mutationFn: () => api.setLeadDnc(leadId, true, reason.trim() || null),
    onSuccess: () => {
      onDone();
      onOpenChange(false);
      setReason('');
      toast.success('Do Not Contact applied');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Apply Do Not Contact</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="mb-3 text-xs text-muted-foreground">
            The lead is removed from every live queue immediately and can never re-enter one. Changing the
            pipeline status later will not clear this.
          </p>
          <Field label="Reason">
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Asked not to be called again"
              autoFocus
            />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" size="sm" loading={apply.isPending} onClick={() => apply.mutate()}>
            Apply DNC
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
